import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Bot, Send, Search, FileText, ExternalLink, Maximize2, Minimize2 } from 'lucide-react';
import { aiService, AIResponse } from '../services/aiService';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: KnowledgeBaseSource[];
}

interface KnowledgeBaseSource {
  id: string;
  title: string;
  url: string;
  excerpt: string;
  space: string;
}

interface KnowledgeChatProps {
  isOpen: boolean;
  onClose: () => void;
}

const KnowledgeChat: React.FC<KnowledgeChatProps> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add welcome message
      setMessages([{
        id: '1',
        type: 'assistant',
        content: 'Hi! I\'m your assistant. I answer using the built‑in knowledge base. What would you like to know?',
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setIsTyping(true);

    try {
      // Simulate AI processing and knowledge base search
      const response = await searchKnowledgeBaseAndGenerateResponse(input.trim());
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response.content,
        timestamp: new Date(),
        sources: response.sources
      };

      // Simulate typing delay
      setTimeout(() => {
        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
        setIsTyping(false);
      }, 1500);

    } catch (error) {
      console.error('Error processing message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'Sorry, I encountered an error while searching the knowledge base. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const searchKnowledgeBaseAndGenerateResponse = async (query: string): Promise<AIResponse> => {
    try {
      // Use the AI service to process the query against the internal knowledge base only
      const response = await aiService.processQuery(query);
      return response;
    } catch (error) {
      console.error('Error processing query:', error);
      // Fallback response
      return {
        content: "I'm sorry, I encountered an error while searching our documentation. Please try again later.",
        sources: [],
        confidence: 0
      };
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${
        isFullscreen ? 'bg-black' : ''
      }`}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className={`bg-white rounded-lg shadow-xl flex flex-col ${
          isFullscreen 
            ? 'w-full h-full max-w-none rounded-none' 
            : 'w-full max-w-6xl h-[80vh]'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Bot className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Knowledge Assistant</h2>
              <p className="text-sm text-gray-500">Powered by AI • Connected to Knowledge Base</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleFullscreen}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <AnimatePresence>
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-xl p-5 ${
                    message.type === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-50 text-gray-900 border border-gray-200'
                  }`}
                >
                  <div className="text-sm prose prose-sm max-w-none leading-relaxed">
                    {message.content.split('\n').map((paragraph, index) => {
                      // Handle different formatting patterns
                      if (paragraph.trim().startsWith('**') && paragraph.trim().endsWith('**')) {
                        // Bold text
                        const boldText = paragraph.trim().slice(2, -2);
                        return (
                          <p key={index} className="mb-2">
                            <strong>{boldText}</strong>
                          </p>
                        );
                      } else if (paragraph.trim().startsWith('- ')) {
                        // Bullet points
                        return (
                          <ul key={index} className="mb-2 ml-4">
                            <li className="list-disc">{paragraph.trim().slice(2)}</li>
                          </ul>
                        );
                      } else if (paragraph.trim().startsWith('1. ') || paragraph.trim().match(/^\d+\. /)) {
                        // Numbered lists
                        return (
                          <ol key={index} className="mb-2 ml-4">
                            <li className="list-decimal">{paragraph.trim().replace(/^\d+\. /, '')}</li>
                          </ol>
                        );
                      } else if (paragraph.trim().startsWith('> ')) {
                        // Blockquotes
                        return (
                          <blockquote key={index} className="mb-2 pl-4 border-l-2 border-gray-300 italic">
                            {paragraph.trim().slice(2)}
                          </blockquote>
                        );
                      } else if (paragraph.trim().startsWith('```')) {
                        // Code blocks
                        return (
                          <pre key={index} className="mb-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                            <code>{paragraph.trim().slice(3)}</code>
                          </pre>
                        );
                      } else if (paragraph.trim() === '') {
                        // Empty lines
                        return <br key={index} />;
                      } else {
                        // Regular paragraphs with inline formatting
                        const formattedText = paragraph
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\*(.*?)\*/g, '<em>$1</em>')
                          .replace(/`(.*?)`/g, '<code class="bg-gray-200 px-1 rounded text-xs">$1</code>');
                        
                        return (
                          <p 
                            key={index} 
                            className="mb-2 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: formattedText }}
                          />
                        );
                      }
                    })}
                  </div>
                  
                  {/* Sources */}
                  {message.sources && message.sources.length > 0 && (
                    <div className="mt-4 border-t border-gray-200 pt-3">
                      <div className="flex items-center space-x-2 mb-3">
                        <Search className="w-4 h-4 text-gray-500" />
                        <p className="text-sm font-medium text-gray-700">Sources:</p>
                      </div>
                      <div className="space-y-3">
                        {message.sources.map((source, index) => (
                          <div key={source.id} className="bg-white bg-opacity-50 rounded-lg p-3 border border-gray-200 hover:shadow-sm transition-shadow">
                            <div className="flex items-start space-x-3">
                              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                                source.type === 'confluence' 
                                  ? 'bg-blue-100' 
                                  : 'bg-green-100'
                              }`}>
                                {source.type === 'confluence' ? (
                                  <FileText className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <FileText className="w-4 h-4 text-green-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  <a
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center space-x-1 group"
                                  >
                                    <span className="truncate">{source.title}</span>
                                    <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
                                  </a>
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    source.type === 'confluence'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-green-100 text-green-700'
                                  }`}>
                                    {source.type === 'confluence' ? '📄 Confluence' : '🎓 Tutorial'}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 mt-1 line-clamp-2 leading-relaxed">
                                  {source.excerpt}
                                </p>
                                <div className="flex items-center space-x-2 mt-2">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                                    {source.type === 'confluence' ? source.space : source.category}
                                  </span>
                                  {source.difficulty && source.type === 'tutorial' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700">
                                      {source.difficulty}
                                    </span>
                                  )}
                                  {source.relevanceScore && (
                                    <span className="text-xs text-gray-500">
                                      Relevance: {Math.round(source.relevanceScore * 100)}%
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Typing indicator */}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start"
            >
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-sm text-gray-600 font-medium">Searching knowledge base...</span>
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex space-x-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about your documentation..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                disabled={isLoading}
              />
              <Search className="absolute right-4 top-3.5 w-5 h-5 text-gray-400" />
            </div>
            <button
              onClick={handleSendMessage}
              disabled={!input.trim() || isLoading}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-base"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default KnowledgeChat;
